import React, { useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import TableImage from "../../shared/Table/TableImage";
import { useSelector } from "react-redux";
import { hasPermission } from "../../utils/adminPermissions";
import { exportTableToCsv } from "../../utils/exportTableCsv";

const formatDate = (date) =>
    date?.toLocaleDateString?.("en-US", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
    });

export default function BannerTable({ heading, allBanners, loading, onUpload, onAdd, onDelete }) {
    const { userProfile } = useSelector((state) => state.profile);
    const canCreate = hasPermission(userProfile, "banners.create");
    const canDelete = hasPermission(userProfile, "banners.delete");

    const [visible, setVisible] = useState(false);
    const [file, setFile] = useState(null);
    const [saving, setSaving] = useState(false);

    const resetDialog = () => {
        setFile(null);
        setVisible(false);
    };

    const handleSave = async () => {
        if (!file) return;
        setSaving(true);
        const path = await onUpload(file);
        if (path) {
            onAdd(path, resetDialog);
        }
        setSaving(false);
    };

    const image = (r) => <TableImage image={r.image} />;

    const actions = (d) => (
        <div className="flex justify-content-around">
            {canDelete && <i className="pi pi-trash cursor-pointer text-dark" onClick={() => onDelete(d._id)}></i>}
        </div>
    );

    const exportBanners = () => {
        exportTableToCsv("banners.csv", allBanners, [
            { header: "Image Path", value: "image" },
            { header: "Type", value: "type" },
            { header: "Added Date", value: (row) => (row.time ? formatDate(row.time) : "-") },
        ]);
    };

    return (
        <div className="col-12">
            <div className="card">
                <div className="flex justify-content-between mb-4">
                    <h5>{heading}</h5>
                    <div className="flex align-items-center gap-2">
                        <Button
                            label="Export CSV"
                            icon="pi pi-download"
                            onClick={exportBanners}
                            disabled={!allBanners.length}
                            severity="success"
                        />
                        {canCreate && <Button label="Add Banner" icon="pi pi-plus" onClick={() => setVisible(true)} />}
                    </div>
                </div>
                <DataTable
                    paginator
                    value={allBanners}
                    rows={50}
                    rowsPerPageOptions={[10, 20, 50, 100]}
                    loading={loading}
                    emptyMessage="No banners added yet."
                    showGridlines
                >
                    <Column body={image} header="Preview" style={{ width: "100px" }}></Column>
                    <Column field="type" header="Placement"></Column>
                    <Column header="Added Date" body={(rowData) => formatDate(rowData.time)} style={{ width: "150px" }}></Column>
                    <Column body={actions} style={{ width: "80px" }} header="Action"></Column>
                </DataTable>
            </div>

            <Dialog
                header="Add Home Screen Carousel Banner"
                visible={visible}
                style={{ width: "40vw" }}
                onHide={resetDialog}
            >
                <div className="flex flex-column gap-3">
                    <p className="text-500 mb-0">
                        Recommended: 1080 × 540px (2:1 ratio). Accepted: 800×400px to 1456×734px.
                        <br />
                        Format: JPG, JPEG, PNG, or WEBP. Max file size: 1MB (recommended 150–350KB).
                    </p>
                    <input
                        type="file"
                        accept="image/jpeg,image/jpg,image/png,image/webp"
                        onChange={(e) => setFile(e.target.files?.[0] || null)}
                    />
                    {file ? <TableImage image={file} /> : null}
                    <div className="flex justify-content-end gap-2 mt-3">
                        <Button label="Cancel" icon="pi pi-times" onClick={resetDialog} className="p-button-outlined" />
                        <Button label="Save" icon="pi pi-check" onClick={handleSave} loading={saving} disabled={!file} />
                    </div>
                </div>
            </Dialog>
        </div>
    );
}
